<template>
  <button
    v-if="qty === 0"
    :id="'batcbm_addbtn_' + item.id"
    type="button"
    class="wc-atc-qty-adjuster-modern-card__atc"
    v-bind="attrs"
    @click.stop="onClickAddToCart"
  >
    <WCSvgIcon name="shopping-cart" :icon-size="{ widthRem: 1.125, heightRem: 1 }" />
    <slot name="atc-label">{{ t('add') }}</slot>
  </button>

  <div
    v-else-if="isRandomWeight"
    class="wc-atc-qty-adjuster-modern-card wc-atc-qty-adjuster-modern-card--weight"
    v-bind="attrs"
  >
    <span class="sr-only" aria-live="polite">{{ t('quantity') }} {{ displayWeight }}</span>

    <button
      type="button"
      class="wc-atc-qty-adjuster-modern-card__minus wc-atc-qty-adjuster-modern-card__trash"
      @click.stop="onClickRemoveWeight"
      :aria-label="t('decreaseQuantity')"
    >
      <WCSvgIcon name="trash" :icon-size="{ widthRem: 0.875, heightRem: 0.875 }" />
    </button>

    <WCMenuDropdownModern
      variant="card"
      :overlay-menu="true"
      :options="weightOptions"
      :label="displayWeight"
      :aria-label="t('quantity')"
      @select="onSelectWeight"
    />
  </div>

  <div v-else class="wc-atc-qty-adjuster-modern-card" v-bind="attrs">
    <span class="sr-only" aria-live="polite">{{ t('quantity') }} {{ displayQty }}</span>

    <button
      type="button"
      class="wc-atc-qty-adjuster-modern-card__minus"
      :class="{ 'wc-atc-qty-adjuster-modern-card__trash': qty <= minStep }"
      @click.stop="onClickDecrement"
      :aria-label="t('decreaseQuantity')"
    >
      <WCSvgIcon
        :name="qty <= minStep ? 'trash' : 'minus'"
        :icon-size="{ widthRem: 0.875, heightRem: 0.875 }"
      />
    </button>

    <button
      v-if="!isEditing"
      type="button"
      class="wc-atc-qty-adjuster-modern-card__qty"
      @click.stop="onQtyClick"
      :aria-label="t('quantity') + ' ' + displayQty"
    >
      <span class="sr-only">{{ t('quantity') }}</span>
      {{ displayQty }}
    </button>
    <input
      v-else
      ref="qtyInputRef"
      v-model.number="inputQty"
      type="number"
      inputmode="decimal"
      class="wc-atc-qty-adjuster-modern-card__qty-input"
      :min="minStep"
      :step="minStep"
      :aria-label="t('quantity')"
      @beforeinput="restrictQtyInput"
      @input="autosizeQtyInput"
      @blur="commitQtyInput"
      @keydown="onQtyKeydown"
      @click.stop
    />

    <button
      ref="incrementButtonRef"
      type="button"
      class="wc-atc-qty-adjuster-modern-card__plus"
      :class="{ 'wc-atc-qty-adjuster-modern-card__plus--disabled': !canIncrement }"
      @click.stop="onClickAddToCart"
      :aria-label="t('increaseQuantity')"
    >
      <WCSvgIcon name="plus" :icon-size="{ widthRem: 0.875, heightRem: 0.875 }" />
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  nextTick,
  onMounted,
  onUnmounted,
  defineProps,
  defineEmits,
  withDefaults,
  useAttrs,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { BigNumber } from 'bignumber.js';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';
import WCMenuDropdownModern from '@/components/WCMenuDropdownModern/WCMenuDropdownModern.vue';
import useCartControls from '@/composables/useCartControls';
import useQuantityDisplay from '@/modules/orders/composables/useQuantityDisplay';
import ItemSummary from '@/models/entities/items/item-summary';
import OrderType from '@/models/entities/cart/order-type';
import { ENTRY_METHOD, EntryMethod } from '@/models/entities/cart/entry-method';
import { useStore } from 'vuex';
import ModalService from '@/modules/modals/services/modal.service';
import ExitScanAndGo from '@/views/ScanProduct/ExitScanAndGo/ExitScanAndGo.vue';

interface PriceEmbeddedLineItem {
  embeddedPrice?: number;
  originalPrice?: number;
  embeddedDiscount?: number;
  expiration?: string;
  embeddedNetWeight?: number;
}

interface Props {
  item: ItemSummary;
  variation?: string | null;
  priceEmbeddedLineItem?: PriceEmbeddedLineItem;
  showStoreSelectModal?: boolean;
  entryMethod?: EntryMethod;
  orderType?: OrderType | null;
}

const props = withDefaults(defineProps<Props>(), {
  variation: undefined,
  priceEmbeddedLineItem: undefined,
  showStoreSelectModal: true,
  entryMethod: ENTRY_METHOD.DEFAULT,
  orderType: undefined,
});

const emit = defineEmits(['addToCart', 'decrement']);
const attrs = useAttrs();
const { t } = useI18n();
const store = useStore();

const isInStore = computed(() => store.getters['cart/getInStore']);
const isCustomerModeScan = computed(() => store.getters['user/isCustomerModeScan']);
const setInStore = (value: boolean) => store.dispatch('cart/setInStore', value);

const {
  getMinimumQuantity,
  getQuantityInCart,
  recoverUnroundedQuantityFromCart,
  getDisplayQuantity,
  isNextQuantityIncrementInCartValid,
  alertMaximumQuantityReached,
  increment,
  decrement,
  setQuantity,
  getValidQuantityFromManualEntry,
  isManualEntryAboveMaximum,
  getWeightOptions,
  setWeight,
} = useCartControls(computed(() => props.orderType));

const { isSoldByWeight, isSoldByRandomWeight } = useQuantityDisplay();

const variation = computed(() => props.variation ?? null);
const quantityInCart = computed(() => getQuantityInCart(props.item, variation.value));

const qty = computed(() => quantityInCart.value.toNumber());
const minStep = computed(() => getMinimumQuantity(props.item).toNumber());

/**
 * The quantity to show in the UI, formatted by the composable's getDisplayQuantity
 * (each-count for average weight, rounded weight for random weight, otherwise the number).
 * showAbbr defaults to false, so no unit is appended.
 */
const displayQty = computed(() => getDisplayQuantity(props.item, quantityInCart.value));

const canIncrement = computed(() =>
  isNextQuantityIncrementInCartValid(props.item, variation.value),
);

const weightedQuantityFor = (item: ItemSummary, target: BigNumber): number | null => {
  if (!isSoldByWeight(item)) {
    return null;
  }
  return target.isZero() ? 0 : 1;
};

const isRandomWeight = computed(() => isSoldByRandomWeight(props.item));

const weightOptions = computed(() => getWeightOptions(props.item, variation.value));

const displayWeight = computed(() => getDisplayQuantity(props.item, quantityInCart.value, true));

const incrementButtonRef = ref<HTMLButtonElement | null>(null);

const HAPTIC_FEEDBACK_DURATION = 200;

const onClickAddToCart = async () => {
  await setInStore(isCustomerModeScan.value);

  if (isCustomerModeScan.value || (!isInStore.value && !isCustomerModeScan.value)) {
    await increment(
      props.item,
      props.variation,
      props.priceEmbeddedLineItem,
      props.showStoreSelectModal,
      props.entryMethod,
    );
    emit('addToCart');
    navigator.vibrate?.(HAPTIC_FEEDBACK_DURATION);

    await nextTick();
    if (!canIncrement.value) {
      alertMaximumQuantityReached();
    }

    if (isInStore.value && !isCustomerModeScan.value) {
      try {
        await ModalService.open(ExitScanAndGo);
      } catch (error) {
        console.error(error);
      }
    }
  }

  await nextTick();
  incrementButtonRef.value?.focus();
};

const onClickDecrement = async () => {
  emit('decrement');
  await decrement(props.item, props.variation);
};

const onSelectWeight = async (value: BigNumber) => {
  const current = quantityInCart.value;
  try {
    await setWeight(
      props.item,
      variation.value,
      value,
      props.priceEmbeddedLineItem,
      props.showStoreSelectModal,
      props.entryMethod,
    );
    emit(value.isGreaterThan(current) ? 'addToCart' : 'decrement');
  } catch (error) {
    console.error(error);
  }
};

const onClickRemoveWeight = async () => {
  emit('decrement');
  try {
    await setQuantity(0, 0, props.item, variation.value, props.priceEmbeddedLineItem);
  } catch (error) {
    console.error(error);
  }
};

const isEditing = ref(false);
const inputQty = ref(0);
const qtyInputRef = ref<HTMLInputElement | null>(null);

const autosizeQtyInput = () => {
  const el = qtyInputRef.value;
  if (!el) {
    return;
  }
  el.style.width = '0';
  el.style.width = `${el.scrollWidth + 2}px`;
};

const onQtyClick = async () => {
  isEditing.value = true;
  inputQty.value = Number(displayQty.value);
  await nextTick();
  qtyInputRef.value?.select();
  autosizeQtyInput();
};

const syncKeyboardInset = () => {
  const viewport = window.visualViewport;
  const keyboardOverlap =
    viewport && isEditing.value
      ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      : 0;
  document.documentElement.style.setProperty('--wc-keyboard-overlap', `${keyboardOverlap}px`);
};
onMounted(() => window.visualViewport?.addEventListener('resize', syncKeyboardInset));
onUnmounted(() => window.visualViewport?.removeEventListener('resize', syncKeyboardInset));

const commitQtyInput = async () => {
  isEditing.value = false;
  syncKeyboardInset();
  const raw = inputQty.value;
  const current = recoverUnroundedQuantityFromCart(props.item, variation.value);

  if (typeof raw !== 'number' || Number.isNaN(raw) || raw < 0) {
    inputQty.value = current.toNumber();
    return;
  }

  if (isManualEntryAboveMaximum(raw, props.item)) {
    inputQty.value = current.toNumber();
    alertMaximumQuantityReached();
    return;
  }

  const target = getValidQuantityFromManualEntry(raw, props.item);
  if (target.isEqualTo(current)) {
    inputQty.value = current.toNumber();
    return;
  }

  try {
    await setQuantity(
      target,
      weightedQuantityFor(props.item, target),
      props.item,
      variation.value,
      props.priceEmbeddedLineItem,
      props.showStoreSelectModal,
      props.entryMethod,
    );
    emit(target.isGreaterThan(current) ? 'addToCart' : 'decrement');
  } catch (error) {
    console.error(error);
    inputQty.value = current.toNumber();
  }
};

const cancelQtyInput = () => {
  isEditing.value = false;
  syncKeyboardInset();
};

const restrictQtyInput = (e: InputEvent) => {
  const data = e.data;
  if (!data) {
    return;
  }
  if (!/^[0-9.]+$/.test(data)) {
    e.preventDefault();
    return;
  }
  const currentText = (e.target as HTMLInputElement).value;
  const dotCount = (currentText.match(/\./g) || []).length + (data.match(/\./g) || []).length;
  if (dotCount > 1) {
    e.preventDefault();
  }
};

const onQtyKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitQtyInput();
  } else if (e.key === 'Escape' || e.key === 'Esc') {
    e.preventDefault();
    cancelQtyInput();
  } else if (['-', '+', 'e', 'E'].includes(e.key)) {
    e.preventDefault();
  }
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

%wc-atc-qty-adjuster-modern-card__pill {
  display: flex;
  width: 100%;
  height: 2.5625rem;
  align-items: center;
  font-size: 1rem;
  font-weight: 700;
  line-height: normal;
}

.wc-atc-qty-adjuster-modern-card__atc {
  @extend %wc-atc-qty-adjuster-modern-card__pill;

  justify-content: center;
  border: none;
  border-top: 0.03125rem solid var(--dark-lighter-9);
  background: var(--white);
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--primary, $primary);
}

@media (hover: hover) {
  .wc-atc-qty-adjuster-modern-card__atc:hover {
    background: var(--primary-lighter-9);
  }
}

.wc-atc-qty-adjuster-modern-card {
  @extend %wc-atc-qty-adjuster-modern-card__pill;

  justify-content: space-between;
  gap: 0.5rem;
  color: var(--primary);
  border-radius: 0 0 0.5rem 0.5rem;
  border: 2px solid var(--primary);
  background: var(--white);
  padding: 0 0.75rem;
}

.wc-atc-qty-adjuster-modern-card--weight :deep(.wc-menu-dropdown-modern__value) {
  color: $black;
}

.wc-atc-qty-adjuster-modern-card--weight :deep(.wc-menu-dropdown-modern__chevron) {
  color: var(--dark-lighter-3);
}

.wc-atc-qty-adjuster-modern-card__minus,
.wc-atc-qty-adjuster-modern-card__plus,
.wc-atc-qty-adjuster-modern-card__qty {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  cursor: pointer;
}

.wc-atc-qty-adjuster-modern-card__minus,
.wc-atc-qty-adjuster-modern-card__plus {
  align-self: stretch;
  position: relative;
}

.wc-atc-qty-adjuster-modern-card__minus {
  justify-content: flex-start;
}

.wc-atc-qty-adjuster-modern-card__plus {
  justify-content: flex-end;
}

.wc-atc-qty-adjuster-modern-card__minus :deep(.wc-icon),
.wc-atc-qty-adjuster-modern-card__plus :deep(.wc-icon) {
  color: inherit;
  position: relative;
  z-index: 1;
}

.wc-atc-qty-adjuster-modern-card__trash :deep(.wc-icon) {
  color: var(--danger, $danger);
}

.wc-atc-qty-adjuster-modern-card__minus::before,
.wc-atc-qty-adjuster-modern-card__plus::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 2.125rem;
  height: 2.125rem;
  border-radius: 50%;
  background: transparent;
  z-index: 0;
  pointer-events: none;
  transition: background 0.1s ease;
}

@media (hover: hover) {
  .wc-atc-qty-adjuster-modern-card__minus:hover::before,
  .wc-atc-qty-adjuster-modern-card__plus:hover::before {
    background: var(--primary-lighter-9);
  }

  .wc-atc-qty-adjuster-modern-card__trash:hover::before {
    background: var(--danger-lighter-9);
  }
}

.wc-atc-qty-adjuster-modern-card__minus:active::before,
.wc-atc-qty-adjuster-modern-card__plus:active::before {
  background: var(--primary-lighter-9);
}

.wc-atc-qty-adjuster-modern-card__trash:active::before {
  background: var(--danger-lighter-9);
}

.wc-atc-qty-adjuster-modern-card__plus--disabled,
.wc-atc-qty-adjuster-modern-card__plus:disabled {
  cursor: not-allowed;
  pointer-events: none;

  .wc-icon {
    opacity: 0.6;
  }
}

.wc-atc-qty-adjuster-modern-card__qty {
  flex: 0 0 auto;
  box-sizing: border-box;
  min-width: 3rem;
  text-align: center;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: normal;
  color: $black;
  background: var(--white);
  border: 1.5px solid var(--dark-lighter-9);
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  cursor: text;

  &:hover {
    border-color: $black;
  }
}

.wc-atc-qty-adjuster-modern-card__qty-input {
  flex: 0 1 auto;
  box-sizing: border-box;
  field-sizing: content;
  min-width: 3rem;
  max-width: 80%;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: $black;
  background: var(--white);
  border: 1.5px solid $black;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;

  &:focus {
    box-shadow: none;
    border-color: $black;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
}

:deep(.wc-menu-dropdown-modern--card .wc-menu-dropdown-modern__list) {
  max-height: 12rem;
}
</style>
